import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { ButtonDirective } from '@coreui/angular-pro';
import { MatomoTrackerService } from 'analytics';
import { I18nService, TranslatePipe } from 'i18n';

import { CheckoutResponse, FunnelService, QuizProduct, QuizSession } from '../../../services/funnel.service';
import { FunnelShellComponent } from '../funnel-shell/funnel-shell.component';

const MATOMO_CATEGORY = 'FAC3LESS Funnel';

type PaymentMethodKey = 'web3_payment' | 'credit_card';

interface PaymentMethodOption {
  key:        PaymentMethodKey;
  i18n_label: string;
  i18n_hint:  string;
  enabled:    boolean;
}

/**
 * Step 1 of the standalone checkout: confirm product + pick payment method.
 * Calls /api/funnel/quiz/checkout (idempotent) to create the Order, then
 * navigates to /quiz/payment where the chosen flow takes over.
 */
@Component({
  selector: 'app-funnel-checkout-page',
  standalone: true,
  imports: [CommonModule, ButtonDirective, TranslatePipe, FunnelShellComponent],
  templateUrl: './funnel-checkout-page.component.html',
  styleUrls: ['./funnel-checkout-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FunnelCheckoutPageComponent {
  private router     = inject(Router);
  private funnel     = inject(FunnelService);
  private i18n       = inject(I18nService);
  private matomo     = inject(MatomoTrackerService);
  private destroyRef = inject(DestroyRef);

  loading = signal(true);
  busy    = signal(false);
  error   = signal<string | null>(null);
  product = signal<QuizProduct | null>(null);
  code    = signal<string | null>(null);

  selected = signal<PaymentMethodKey>('web3_payment');

  // Phase 5 ships web3_payment only. credit_card is wired in the UI but disabled
  // until the Stripe integration is added — see plan, "credit_card coming soon".
  readonly methods: PaymentMethodOption[] = [
    { key: 'web3_payment', i18n_label: 'funnel.checkout.method.web3.label', i18n_hint: 'funnel.checkout.method.web3.hint', enabled: true },
    { key: 'credit_card',  i18n_label: 'funnel.checkout.method.card.label', i18n_hint: 'funnel.checkout.method.card.hint',  enabled: false }
  ];

  resultKey = computed(() => {
    const c = this.code();
    return c ? `funnel.result.${c.toLowerCase()}` : null;
  });

  constructor() {
    // No local-token guard: the backend can resume via the `cid` auth cookie
    // and re-issues a session token; only a 401/404 sends the user back to /r.
    this.loadSession();
  }

  selectMethod(key: PaymentMethodKey, enabled: boolean): void {
    if (!enabled) return;
    this.selected.set(key);
  }

  onProceed(): void {
    if (this.busy()) return;
    const method = this.selected();
    if (!this.methods.find(m => m.key === method)?.enabled) return;

    this.busy.set(true);
    this.error.set(null);

    this.funnel.createCheckout(method)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: CheckoutResponse) => {
          this.busy.set(false);
          this.matomo.trackEvent({
            category: MATOMO_CATEGORY,
            action:   'checkout_started',
            name:     `${res.product.code}|${method}`,
            value:    Number(res.total_price) || undefined
          });
          const lang = this.i18n.getLanguage() || 'en';
          // Stash the order id for the payment page (state) so it can drive payment without re-creating.
          this.router.navigate(
            ['/', lang, 'quiz', 'payment'],
            { state: { order: res } }
          );
        },
        error: err => {
          this.busy.set(false);
          this.error.set(err?.error?.error || 'Could not start checkout. Please try again.');
        }
      });
  }

  onBack(): void {
    const lang = this.i18n.getLanguage() || 'en';
    this.router.navigate(['/', lang, 'quiz', 'result']);
  }

  private loadSession(): void {
    this.funnel.showQuiz()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (session: QuizSession) => {
          this.loading.set(false);
          if (session.session_token) this.funnel.storeSessionToken(session.session_token);
          if (session.status === 'purchased') {
            // already done — bounce to success
            const lang = this.i18n.getLanguage() || 'en';
            this.router.navigate(['/', lang, 'quiz', 'success'], { replaceUrl: true });
            return;
          }
          if (session.status !== 'completed') {
            const lang = this.i18n.getLanguage() || 'en';
            this.router.navigate(['/', lang, 'quiz'], { replaceUrl: true });
            return;
          }
          this.code.set(session.recommended_product_code || null);
          this.product.set(session.product || null);
        },
        error: err => {
          this.loading.set(false);
          if (err?.status === 401 || err?.status === 404) {
            this.funnel.clearSessionToken();
            this.redirectToLanding();
            return;
          }
          this.error.set(err?.error?.error || 'Could not load checkout.');
        }
      });
  }

  private redirectToLanding(): void {
    const lang = this.i18n.getLanguage() || 'en';
    this.router.navigate(['/', lang, 'r'], { replaceUrl: true });
  }
}
